/**
 * Shapes returned by the admin API.
 *
 * Hand-written from the controllers in `backend/src/core/admin/**` (and
 * `backend/src/game/admin/` for meta), because nothing generates them: the
 * backend returns Prisma rows directly and there is no shared schema package
 * between the two halves of this repo.
 *
 * That makes every type here a CLAIM about the server rather than a fact, and
 * the frontend tests mock the API, so they can never falsify one. The contract
 * is checked by hitting the running stack — see `admin-api.test.ts`'s header
 * for the exact calls and what they proved.
 *
 * Fields are typed only as far as the panel reads them. Where a controller
 * returns a whole Prisma row with two dozen columns, the interface lists the
 * ones rendered and nothing else; an unlisted column is not a missing field,
 * it is a column the panel has no opinion about.
 */

/** The envelope every paginated admin list returns. */
export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export interface AdminStats {
  totalUsers: number;
  newToday: number;
  newThisWeek: number;
  pendingWithdrawals: number;
  pendingKyc: number;
}

export interface ServerStatus {
  dbHealthy: boolean;
  uptimeSeconds: number;
  nodeVersion: string;
  memoryMb: number;
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface AdminUserRow {
  id: string;
  email: string;
  username: string | null;
  role: string;
  banned: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  softBalance: number;
  premiumBalance: number;
  level: number;
  kycStatus: string;
}

/**
 * `GET /admin/users/:id` returns the row with `omit: { password: true }` — so
 * every other User column, whatever the game has added to the model, plus a
 * `_count`. Typed as an index signature on purpose: the detail drawer renders
 * whatever it is given rather than a fixed list that would silently stop
 * showing new columns.
 */
export interface AdminUserDetail extends AdminUserRow {
  _count?: { payments: number };
  [column: string]: unknown;
}

export type CurrencyCode = 'SOFT' | 'PREMIUM';

export interface UsersQuery {
  page?: number;
  search?: string;
  /** Omitted means "all". The API also accepts the literal 'all'. */
  status?: 'active' | 'banned' | 'all';
}

// ─── Player events (the per-user audit trail) ────────────────────────────────

export interface PlayerEvent {
  id: string;
  category: string;
  eventType: string;
  title: string;
  description: string;
  eventAt: string;
  ipAddress: string | null;
  geo: string | null;
  metadata: Record<string, unknown> | null;
}

/** The events controller is the one that does NOT use the `Paged` envelope. */
export interface PlayerEventPage {
  data: PlayerEvent[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

// ─── KYC ──────────────────────────────────────────────────────────────────────

export interface KycRow {
  id: string;
  email: string;
  username: string | null;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  kycFullName: string | null;
  kycCountry: string | null;
  kycSubmittedAt: string | null;
  kycReviewedAt: string | null;
}

// ─── Withdrawals ──────────────────────────────────────────────────────────────

export interface WithdrawalRow {
  id: string;
  userId: string;
  amountCents: number;
  currency: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  payoutMethod: string | null;
  payoutDetails: Record<string, string> | null;
  reason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  user: { email: string; username: string | null };
}

export interface WithdrawalPage extends Paged<WithdrawalRow> {
  pendingTotalCents: number;
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

export interface InvoiceRow {
  id: string;
  number: string;
  type: 'IN' | 'OUT' | string;
  amountCents: number;
  currency: string;
  description: string | null;
  paymentMethod: string | null;
  createdAt: string;
  user: { email: string; username: string | null };
}

export interface GenerateInvoiceBody {
  userId: string;
  type: 'IN' | 'OUT';
  amountCents: number;
  description?: string;
  vatRate?: number;
  currency?: string;
  recipientName?: string;
  recipientCompany?: string;
  recipientCode?: string;
  paymentMethod?: string;
}

// ─── CMS ──────────────────────────────────────────────────────────────────────

export interface FaqEntry {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
  active: boolean;
}

export interface LegalPage {
  id: string;
  slug: string;
  title: string;
  content: string;
  active: boolean;
}

export interface SocialLink {
  id: string;
  platform: string;
  url: string;
  icon: string | null;
  sortOrder: number;
  active: boolean;
}

export interface NewsRow {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  tag: string | null;
  published: boolean;
  publishedAt: string | null;
  imageUrl: string | null;
  createdAt: string;
}

// ─── Settings ─────────────────────────────────────────────────────────────────

/** `GET /admin/settings` returns a flat key→value map; values are always strings. */
export type SettingsMap = Record<string, string>;

/** `GET /admin/settings/company` — canonical MAD fields, all strings. */
export interface CompanyInfo {
  name: string;
  addressLine1: string;
  city: string;
  postalCode: string;
  country: string;
  email: string;
  phone: string;
  website: string;
  registrationNumber: string;
  vatNumber: string;
  bankName: string;
  bankAccount: string;
  bankSwift: string;
  [extra: string]: string;
}

/** `GET /admin/settings/commissions` — every value is a number. */
export type CommissionsMap = Record<string, number>;

// ─── Meta tags ────────────────────────────────────────────────────────────────

/**
 * Mirrors `ICON_SLOTS` in `backend/src/core/meta/meta.types.ts`.
 *
 * Duplicated rather than imported: the two halves of the repo do not share a
 * package, and the backend copy is inside `core/`, which this repo may not
 * edit. `meta-tab.test.ts` is what keeps them honest — if a slot is added
 * upstream, `GET /game-admin/meta` returns a key this list does not carry and
 * the panel simply would not show it, which is exactly the silent-content
 * failure mode worth a test.
 */
export const META_ICON_SLOTS = [
  { key: 'icon_ico', label: 'favicon.ico', accept: '.ico', hint: 'any size' },
  { key: 'icon_png_16', label: '16x16 PNG', accept: 'image/png', hint: 'exactly 16x16' },
  { key: 'icon_png_32', label: '32x32 PNG', accept: 'image/png', hint: 'exactly 32x32' },
  { key: 'icon_png_100', label: '100x100 PNG', accept: 'image/png', hint: 'exactly 100x100' },
  { key: 'icon_png_180', label: '180x180 PNG', accept: 'image/png', hint: 'exactly 180x180' },
  { key: 'icon_png_240', label: '240x240 PNG', accept: 'image/png', hint: 'exactly 240x240' },
] as const;

export type MetaIconSlot = (typeof META_ICON_SLOTS)[number]['key'];

/**
 * The text fields the panel edits.
 *
 * `image_width` / `image_height` are deliberately absent: the backend derives
 * them from the uploaded file's own header and REJECTS them on the bulk-update
 * endpoint, so offering them as inputs would build a form that always 400s.
 */
export const META_TEXT_FIELDS = [
  { key: 'title', label: 'Title', hint: 'Falls back to BRAND_NAME' },
  { key: 'description', label: 'Description', hint: 'Up to 500 characters', long: true },
  { key: 'site_name', label: 'Site name', hint: 'Falls back to the title' },
  { key: 'url', label: 'Canonical URL', hint: 'Falls back to APP_URL' },
  { key: 'locale', label: 'Locale', hint: 'Falls back to en_US' },
  { key: 'image', label: 'Share image URL', hint: 'Set by the upload below, or pasted' },
  { key: 'image_alt', label: 'Share image alt text', hint: '' },
  { key: 'twitter_card', label: 'Twitter card', hint: 'summary_large_image' },
] as const;

export type MetaTextField = (typeof META_TEXT_FIELDS)[number]['key'];

export interface MetaResponse {
  /** Raw stored `meta.*` rows, without the prefix. Absent keys are unset. */
  values: Record<string, string>;
  /** What the site would actually serve, after fallbacks. */
  resolved: Record<string, unknown>;
}

// ─── Payments config ─────────────────────────────────────────────────────────

export interface DepositMethodState {
  id: string;
  label: string;
  icon: string;
  provider: string;
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  currencyOptions: string[];
  billing: 'required' | 'optional';
  minBilling: 'required' | 'optional';
}

export interface PayoutMethodState {
  id: string;
  label: string;
  icon: string;
  provider: string;
  receiver: 'cardNumber' | 'iban';
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  billing: 'required';
  pollable: boolean;
}

export interface PaymentLimits {
  depositMinCents: number;
  depositMaxCents: number;
  depositFeeBps: number;
  depositFeePct: number;
  depositKycThresholdCents: number;
  requireVerifiedEmail: boolean;
  withdrawalMinCents: number;
  withdrawalMaxCents: number;
  withdrawalFeeBps: number;
  withdrawalFeePct: number;
  capToDeposits: boolean;
}

export interface PaymentConfigSnapshot {
  adopted: boolean;
  currencies: string[];
  defaultCurrency: string;
  withdrawalsEnabled: boolean;
  kycEnabled: boolean;
  deposit: DepositMethodState[];
  payout: PayoutMethodState[];
  limits: PaymentLimits;
  invoices: {
    prefix: string;
    creditNotePrefix: string;
    emailEnabled: boolean;
    redateBackdateDays: number;
    redateFutureHours: number;
  };
  webhooks: {
    deposit: string;
    payout: string;
  };
}

// ─── API keys ─────────────────────────────────────────────────────────────────

export interface ApiKeyRow {
  id: string;
  label: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  createdBy: { username: string | null; email: string } | null;
}

/** The only response that ever carries the full key — it is never readable again. */
export interface CreatedApiKey {
  id: string;
  label: string;
  prefix: string;
  key: string;
}
